import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { billingPrompts, billingPurchaseAddons, billingSnoozePrompt, errorMessage } from "./api";
import { track } from "./analytics";

// In-app offers. The server decides what is eligible and remembers "Not now" for 30 days; this shows at most ONE per session.
// Buying is one tap, with the card already on file.
const SESSION_KEY = "hv.prompt.shown";

export default function PromptHost({ token, goPlans, goBilling, onChanged }) {
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    try { if (window.sessionStorage.getItem(SESSION_KEY)) return undefined; } catch { /* no storage: show it, once, below */ }
    billingPrompts(token).then((r) => {
      const first = r.prompts && r.prompts[0];
      if (!alive || !first) return;
      try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
      setPrompt(first);
      track("prompt_shown", { prompt: first.id.split(":")[0] }, token);
    }).catch(() => {});
    return () => { alive = false; };
  }, [token]);

  if (!prompt) return null;
  const kind = prompt.id.split(":")[0];
  const accept = async () => {
    track("prompt_accepted", { prompt: kind }, token);
    const a = prompt.action;
    if (a.kind === "plans") { goPlans(); setPrompt(null); return; }
    if (a.kind === "billing") { goBilling(); setPrompt(null); return; }
    setBusy(true);
    try {
      await billingPurchaseAddons(token, [{ addon_id: a.addon_id, ...(a.quantity && a.addon_id === "story_topup" ? { quantity: a.quantity } : {}), ...(a.member_id ? { member_id: a.member_id } : {}) }]);
      toast.success("Added. You'll find it under Add-ons in your profile.");
      setPrompt(null); onChanged?.();
    } catch (e) { toast.error(errorMessage(e, "We couldn't add that. Nothing was charged.")); } finally { setBusy(false); }
  };
  const later = async () => { setPrompt(null); try { await billingSnoozePrompt(token, prompt.id); } catch { /* it will simply come back next session */ } };

  return (
    <div className="fixed inset-x-4 bottom-4 z-40 sm:left-auto sm:right-6 sm:w-96" role="dialog" aria-labelledby="prompt-title" data-testid="prompt">
      <div className="rounded-2xl border border-forest-700 bg-white p-5 shadow-xl">
        <h2 id="prompt-title" className="font-display text-lg font-semibold text-forest-700" data-testid="prompt-title">{prompt.title}</h2>
        <p className="mt-1 text-sm text-foreground/70">{prompt.body}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" disabled={busy} onClick={accept} data-testid="prompt-accept"
            className="min-h-[44px] flex-1 rounded-[10px] bg-forest-700 px-4 text-sm font-semibold text-sand-100 hover:bg-forest-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 disabled:opacity-60">{busy ? "Adding…" : prompt.cta}</button>
          <button type="button" onClick={later} data-testid="prompt-later"
            className="min-h-[44px] rounded-[10px] border border-sand-300 px-4 text-sm font-semibold text-forest-700 hover:bg-sand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500">Not now</button>
        </div>
      </div>
    </div>
  );
}
